/* The new header search, the Intelligems search test treatment: PredictiveSearch (theme.js) presents through it while <html> has .search-v2. */
(function () {
  'use strict';

  // Without its parts it would fail mid-search; unregistered, theme.js falls back to the original search.
  if (theme.searchOverlay || !theme.searchOverlayParts || !theme.searchEmptyStates || !theme.searchOverlayKeys) return;

  const HISTORY_LIMIT = 3;
  // Shopify's predictive search maximum of each kind, so categories with products to show (and the shopper's) have candidates.
  const CANDIDATE_LIMIT = 10;
  // Its own sections: results with categories and a pinned "show all" (every candidate where a machine ranks them), and recently
  // viewed rows with SKUs, as results show them.
  const RESULTS_SECTION = 'search-results-v2';
  const RANKED_RESULTS_SECTION = 'search-results-v2-ranked';
  const CARD_SECTION = 'predictive-search-recently-viewed-card-v2';
  const RESULTS_ROOT = '.predictive-search-results';
  const EMPTY_STATES = '.predictive-search-results--zero, .predictive-search-results--none';
  // Typed results a loading state keeps on screen; anything else (zero state, skeleton, error) is replaced by skeletons.
  const KEPT_RESULTS = '.predictive-search-results:not(.predictive-search-results--zero,.predictive-search-results--skeleton,.predictive-search-results--error)';
  // Past Android's 8 dp touch slop (ViewConfiguration), a moving finger is a drag, not a tap.
  const DRAG_SLOP_PX = 10;

  // The phone sheet runs from the bar to 100dvh, which iOS and Android keep under the keyboard. The bar moves with a banner above
  // the header (a dealer's notice) and with the sticky header, which slides in after the scroll that pinned it (theme.js).
  function followBarTop(search) {
    if (search.barTop) return;
    const header = search.closest('#SiteHeader');
    const measure = () => {
      search.barTop.frame = 0;
      search.predictiveSearchResults.style.setProperty('--searchBarTop', `${search.getBoundingClientRect().top}px`);
    };
    const schedule = () => { search.barTop.frame = search.barTop.frame || requestAnimationFrame(measure); };
    const slid = (event) => { if (event.target === header) schedule(); };
    search.barTop = { frame: 0, header, schedule, slid };
    measure();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    if (header) header.addEventListener('transitionend', slid);
  }

  // The last top stays set, so the sheet keeps its size through the exit fade.
  function unfollowBarTop(search) {
    if (!search.barTop) return;
    const { frame, header, schedule, slid } = search.barTop;
    cancelAnimationFrame(frame);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('scroll', schedule);
    if (header) header.removeEventListener('transitionend', slid);
    search.barTop = null;
  }

  // A drag on the results drops the keyboard, as iOS search lists and Safari's own suggestions do: the sheet runs on under the
  // keyboard, so its last rows and "show all" come into reach. Bound once: the panel element outlives the panels shown in it.
  function dismissKeyboardOnDrag(search) {
    if (search.dismissesKeyboardOnDrag) return;
    search.dismissesKeyboardOnDrag = true;
    const results = search.predictiveSearchResults;
    let startY = null;
    results.addEventListener('touchstart', (event) => { startY = event.touches[0].clientY; }, { passive: true });
    results.addEventListener('touchmove', (event) => {
      if (startY === null || Math.abs(event.touches[0].clientY - startY) < DRAG_SLOP_PX) return;
      startY = null;
      if (document.activeElement === search.input) search.input.blur();
    }, { passive: true });
  }

  function open(search) {
    followBarTop(search);
    dismissKeyboardOnDrag(search);
    // WebKit can refuse focus just after the bar's visibility transition ends; reading layout first brings its style up to date.
    search.input.offsetWidth;
    // Inside the opening tap: iOS raises its keyboard for no other focus, and this focus renders the dropdown with the bar.
    search.input.focus({ preventScroll: true });
    theme.searchEmptyStates.prefetch(search);
  }

  function show(search, message) {
    setOpen(search, true);
    search.predictiveSearchResults.removeAttribute('aria-busy');
    theme.searchOverlayKeys.markFresh(search);
    announce(search, message);
    theme.searchOverlayParts.focusMachineSwitch(search);
  }

  // The panel keeps its content, hidden, so reopening shows it again at once (PredictiveSearch reuses what is still current).
  function hide(search, wasOpen) {
    // Counted per search, so a machine sheet still waiting on a tap from this search knows it has closed since.
    search.closeCount = (search.closeCount || 0) + 1;
    unfollowBarTop(search);
    setOpen(search, false);
    search.predictiveSearchResults.removeAttribute('aria-busy');
    theme.searchOverlayKeys.resetHighlight(search);
    // A closed search has no results to report; the last count mustn't linger in the status region.
    const status = search.querySelector('[data-search-status]');
    if (status) status.textContent = '';
    if (wasOpen && search.trigger) theme.searchOverlayKeys.returnFocus(search);
  }

  function clear(search) {
    setOpen(search, false);
    search.predictiveSearchResults.removeAttribute('aria-busy');
    search.predictiveSearchResults.replaceChildren();
  }

  function setOpen(search, isOpen) {
    if (isOpen) reveal(search.predictiveSearchResults);
    else search.predictiveSearchResults.classList.remove('is-open');
    search.input.setAttribute('aria-expanded', String(isOpen));
  }

  // The original search leaves an inline display (none) when it closes, as on any Escape; once this search presents, it owns visibility.
  function reveal(results) {
    results.style.removeProperty('display');
    results.classList.add('is-open');
  }

  // Typing keeps the current results on screen, marked busy, while the next ones load; with none to keep, skeletons stand in.
  // A skeleton has no options, so the field reports the list collapsed until results arrive (show).
  function loading(search) {
    const results = search.predictiveSearchResults;
    results.setAttribute('aria-busy', 'true');
    if (results.querySelector(KEPT_RESULTS)) return;
    if (!results.querySelector('.predictive-search-results--skeleton')) results.replaceChildren(theme.searchOverlayParts.buildSkeleton());
    reveal(results);
    search.input.setAttribute('aria-expanded', 'false');
  }

  // An edit makes the highlight, the options on screen and any request for the earlier text stale: Enter must search what was typed.
  function edited(search) {
    theme.searchOverlayKeys.markStale(search);
    search.abortSearch();
  }

  // A failed or slow search leaves the shopper a way on: the full results page for what they typed.
  function failed(search, query) {
    const label = text(search, 'viewMore').replace('[terms]', () => query);
    search.predictiveSearchResults.replaceChildren(theme.searchOverlayParts.buildError(text(search, 'error'), label));
    show(search, text(search, 'error'));
  }

  // Matches that fit the shopper's machine, which predictive search's ten candidates can miss (search-machine-results.js).
  function fetchCandidates(search, searchTerm, signal) {
    return theme.searchMachineResults ? theme.searchMachineResults.start(search, searchTerm, signal) : null;
  }

  function addCandidates(section, pending) {
    return pending ? theme.searchMachineResults.add(section, pending) : Promise.resolve();
  }

  // Typed results' categories follow the shopper's machine (search-machine-categories.js); a search with no matches gets
  // categories and best sellers instead of a dead end (search-empty-states.js).
  async function prepare(search, section, searchTerm) {
    const none = section.querySelector('.predictive-search-results--none');
    if (none) theme.searchEmptyStates.prepareNone(search, none);
    else if (theme.searchMachineCategories) theme.searchMachineCategories.prepare(search, section, searchTerm);
    return section;
  }

  // Before any panel is shown, the shopper's machine (or a way to add one) leads it on a strip of its own: it's the scope of
  // everything below. With nothing typed or found there's room for a guest's strip to end with the reason to sign in.
  function decorate(search, panel) {
    const root = panel.matches(RESULTS_ROOT) ? panel : panel.querySelector(RESULTS_ROOT);
    const fitment = theme.searchFitment;
    const status = root && fitment && fitment.machineStatus ? fitment.machineStatus() : null;
    const legend = status && theme.searchOverlayParts.buildMachine(search, status, root.matches(EMPTY_STATES));
    if (!legend) return;
    root.prepend(legend);
    theme.searchOverlayParts.followMachineLinks(search);
  }

  // Counts only for a search: an empty field's panel (recent searches, products) isn't a result anyone asked for.
  function announce(search, message) {
    const status = search.querySelector('[data-search-status]');
    if (!status) return;
    const count = theme.searchOverlayKeys.visibleOptions(search).length;
    // The singular and plural strings arrive rendered for 1 and 2 (snippets/predictive-search.liquid); the live count replaces the number.
    const counted = search.searchTerm() ? text(search, count === 1 ? 'resultOne' : 'resultOther').replace(/\d+/, count) : '';
    status.textContent = message || counted;
  }

  // Strings come from data-text-* attributes Liquid translates (snippets/predictive-search.liquid).
  function text(search, key) {
    return search.dataset[`text${key[0].toUpperCase()}${key.slice(1)}`] || '';
  }

  // boundsWaits and reusesResults switch on PredictiveSearch's timeout, bounded fallbacks and caches; empty states: search-empty-states.js.
  const emptyStates = theme.searchEmptyStates;
  theme.searchOverlay = {
    historyLimit: HISTORY_LIMIT, resultsSection: RESULTS_SECTION, rankedResultsSection: RANKED_RESULTS_SECTION, cardSection: CARD_SECTION,
    candidateLimit: CANDIDATE_LIMIT, boundsWaits: true, reusesResults: true, open, show, hide, clear, loading, edited, failed, prepare,
    decorate, keydown: theme.searchOverlayKeys.keydown, fetchCandidates, addCandidates, zeroStateFallback: emptyStates.fallback,
    emptyStateCategories: emptyStates.categories, warm: emptyStates.prefetch,
  };
})();
